/**
 * Formatter Registry
 * 
 * Manages registration and retrieval of field formatters, providing a centralized
 * system for accessing appropriate formatters based on field types.
 */

import { BaseFormatter } from './base-formatter';
import { PhoneFormatter } from './phone-formatter';
import { ZipFormatter } from './zip-formatter';
import { DateFormatter } from './date-formatter';
import { CurrencyFormatter } from './currency-formatter';
import { SSNFormatter } from './ssn-formatter';
import { FieldType } from '../detection-patterns';

/**
 * Formatter registry for managing all field formatters
 */
export class FormatterRegistry {
  private formatters = new Map<string, BaseFormatter>();
  private fieldTypeMap = new Map<FieldType, BaseFormatter>();

  constructor() {
    this.registerDefaultFormatters();
  }

  /**
   * Register all default formatters
   */
  private registerDefaultFormatters(): void {
    // Register individual formatters
    this.registerFormatter('phone', new PhoneFormatter());
    this.registerFormatter('zip', new ZipFormatter());
    this.registerFormatter('date', new DateFormatter());
    this.registerFormatter('currency', new CurrencyFormatter());
    this.registerFormatter('ssn', new SSNFormatter());

    // Map field types to formatters
    this.mapFieldTypeToFormatter(FieldType.PHONE, 'phone');
    this.mapFieldTypeToFormatter(FieldType.ZIP, 'zip');
    this.mapFieldTypeToFormatter(FieldType.DATE_OF_BIRTH, 'date');
    this.mapFieldTypeToFormatter(FieldType.CURRENCY, 'currency');
    this.mapFieldTypeToFormatter(FieldType.SSN, 'ssn');
  }

  /**
   * Register a formatter with a unique identifier
   */
  public registerFormatter(id: string, formatter: BaseFormatter): void {
    this.formatters.set(id, formatter);
  }

  /**
   * Map a field type to a formatter
   */
  public mapFieldTypeToFormatter(fieldType: FieldType, formatterId: string): void {
    const formatter = this.formatters.get(formatterId);
    if (formatter) {
      this.fieldTypeMap.set(fieldType, formatter);
    }
  }

  /**
   * Get formatter by ID
   */
  public getFormatter(id: string): BaseFormatter | undefined {
    return this.formatters.get(id);
  }

  /**
   * Get formatter by field type
   */
  public getFormatterByFieldType(fieldType: FieldType): BaseFormatter | undefined {
    return this.fieldTypeMap.get(fieldType);
  }

  /**
   * Get formatter that can handle a specific field type string
   */
  public getFormatterForFieldType(fieldType: string): BaseFormatter | undefined {
    // Check direct field type mapping first
    const enumFieldType = this.stringToFieldType(fieldType);
    if (enumFieldType !== FieldType.UNKNOWN) {
      return this.fieldTypeMap.get(enumFieldType);
    }

    // Fallback: check if any formatter can handle this field type
    for (const formatter of this.formatters.values()) {
      if (formatter.canHandle(fieldType)) {
        return formatter;
      }
    }

    return undefined;
  }

  /**
   * Get all registered formatters
   */
  public getAllFormatters(): Array<{ id: string; formatter: BaseFormatter }> {
    return Array.from(this.formatters.entries()).map(([id, formatter]) => ({ id, formatter }));
  }

  /**
   * Get all supported field types
   */
  public getSupportedFieldTypes(): FieldType[] {
    return Array.from(this.fieldTypeMap.keys());
  }

  /**
   * Check if a field type is supported
   */
  public isFieldTypeSupported(fieldType: string | FieldType): boolean {
    if (typeof fieldType === 'string') {
      return this.getFormatterForFieldType(fieldType) !== undefined;
    }
    return this.fieldTypeMap.has(fieldType);
  }

  /**
   * Get formatter info for all registered formatters
   */
  public getFormatterInfo(): Array<{ id: string; type: string; pattern: string }> {
    return Array.from(this.formatters.entries()).map(([id, formatter]) => ({
      id,
      type: formatter.getFormatterInfo().type,
      pattern: formatter.getFormatPattern()
    }));
  }

  /**
   * Convert string field type to enum
   */
  private stringToFieldType(fieldType: string): FieldType {
    switch (fieldType.toLowerCase()) {
      case 'phone':
      case 'telephone':
        return FieldType.PHONE;
      case 'zip':
      case 'zipcode':
      case 'postal':
      case 'address_zip':
        return FieldType.ZIP;
      case 'date':
      case 'date_of_birth':
      case 'dob':
        return FieldType.DATE_OF_BIRTH;
      case 'currency':
      case 'money':
      case 'income':
      case 'housing_payment':
        return FieldType.CURRENCY;
      case 'ssn':
      case 'social_security':
        return FieldType.SSN;
      default:
        return FieldType.UNKNOWN;
    }
  }
}

/**
 * Global formatter registry instance
 */
let globalRegistry: FormatterRegistry | null = null;

/**
 * Get the global formatter registry instance
 */
export function getFormatterRegistry(): FormatterRegistry {
  if (!globalRegistry) {
    globalRegistry = new FormatterRegistry();
  }
  return globalRegistry;
}

/**
 * Reset the global formatter registry (primarily for testing)
 */
export function resetFormatterRegistry(): void {
  globalRegistry = null;
}

/**
 * Convenience function to get a formatter by field type
 */
export function getFormatterForField(fieldType: string | FieldType): BaseFormatter | undefined {
  return getFormatterRegistry().getFormatterForFieldType(fieldType as string);
}

/**
 * Convenience function to check if formatting is supported for a field type
 */
export function isFormattingSupported(fieldType: string | FieldType): boolean {
  return getFormatterRegistry().isFieldTypeSupported(fieldType);
}